import {
  type ControlsInput,
  HeadlessRichTextEditor,
  RteUiProvider,
  type HeadlessRichTextEditorProps,
  type RteUiAdapter,
} from '@shared/rich-text-editor-core';
import { useMemo } from 'react';
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form';

import ControlButton from './controls/control-button';
import TailwindTableCellMenu from './ui/TableCellMenu';
import { TailwindRoot, TailwindContent, TailwindLoadingOverlay } from './ui/TailwindPrimitives';
import TailwindToolbar from './ui/Toolbar';

export type FormRichTextEditorProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    HeadlessRichTextEditorProps & {
      /**
       * If true, there will be a red asterisk on the label
       */
      required?: boolean;
      /**
       * The label of the form field
       */
      label?: string;
      /**
       * The helper text of the form field
       */
      helperText?: string;
    };

/* Default Controls Layout (kept the same) */
const DEFAULT_CONTROLS: ControlsInput = [
  ['bold', 'italic', 'underline'],
  ['alignLeft', 'alignCenter', 'alignRight', 'subscript', 'superscript'],
  ['bulletList', 'numberList'],
  ['image', 'table', 'latex', 'beta', 'sd', 'summation'],
];

export default function FormRichTextEditor<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<FormRichTextEditorProps<TFieldValues>>,
) {
  const {
    name,
    controls,
    required: isRequired,
    label,
    helperText,
    onImageUpload,
    loading: isLoading,
    placeholder,
    defaultValue,
    rows,
    ...restProps
  } = props;

  const {
    field: { ref: fieldRef, onChange, onBlur, value },
    fieldState: { error },
  } = useController({ ...restProps, name });

  const formHelperText = error?.message ?? helperText;
  const effectiveControls = controls ?? DEFAULT_CONTROLS;

  const adapter = useMemo(() => {
    return {
      components: {
        Root: TailwindRoot,
        Toolbar: () => <TailwindToolbar controls={effectiveControls} />,
        ControlGroup: ({ children }: { children?: React.ReactNode }) => (
          <div className='inline-flex divide-x divide-neutral-200 overflow-hidden rounded-md border border-neutral-300 bg-white shadow-sm'>
            {children}
          </div>
        ),
        ControlButton: ControlButton,
        Content: TailwindContent,
        LoadingOverlay: TailwindLoadingOverlay,
        Icons: {},
        TableCellMenu: TailwindTableCellMenu,
      },
    } as RteUiAdapter;
  }, [effectiveControls]);

  return (
    <div className='flex flex-col gap-1'>
      {label ? (
        <label className='flex items-center gap-1 text-sm font-medium text-gray-700'>
          <span>{label}</span>
          {isRequired ? (
            <span className='text-red-500' aria-hidden='true'>
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <RteUiProvider adapter={adapter}>
        <HeadlessRichTextEditor
          key={JSON.stringify(effectiveControls)}
          controls={effectiveControls}
          onChange={onChange}
          onBlur={onBlur}
          onImageUpload={onImageUpload}
          loading={isLoading}
          placeholder={placeholder}
          value={value ?? defaultValue}
          rows={rows}
          error={!!error}
          ref={(ref: HTMLDivElement) => {
            fieldRef({
              focus: () => ref?.scrollIntoView({ block: 'center' }),
            });
          }}
        />
      </RteUiProvider>
      {formHelperText ? (
        <label className='flex items-center gap-1 text-sm font-medium text-gray-700'>
          {formHelperText}
        </label>
      ) : null}
    </div>
  );
}
